// Shorten long links (#734): a bare link in a message with a long query string is drawn short (`shared/src/shortUrl.ts`). Display
// only: opening, copying and the stored message use the full link. On by default.
//
// The setting is per device: Settings → Appearance → Shorten long links. localStorage `bloom:short-links`, like Colorized names.
// Storage can be missing or throw (private windows, blocked site data): then the choice lasts until reload.
import { useSyncExternalStore } from "react";

export const SHORT_LINKS_KEY = "bloom:short-links";

/** A stored value as the setting: only "off" turns it off; anything else (missing, old, edited by hand) is the default, on. */
export function parseShortLinks(raw: unknown): boolean {
  return raw !== "off";
}

function stored(): boolean {
  try {
    return parseShortLinks(localStorage.getItem(SHORT_LINKS_KEY));
  } catch {
    return true;
  }
}

const listeners = new Set<() => void>();
let current = stored();

export const getShortLinks = () => current;

export function setShortLinks(next: boolean) {
  current = next;
  try {
    localStorage.setItem(SHORT_LINKS_KEY, next ? "on" : "off");
  } catch {
    // private mode: still applies for this page
  }
  for (const fn of listeners) fn();
}

/** Another tab changed it. */
export function installShortLinks() {
  current = stored();
  addEventListener("storage", (e) => {
    if (e.key !== SHORT_LINKS_KEY) return;
    current = stored();
    for (const fn of listeners) fn();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useShortLinks = (): boolean => useSyncExternalStore(subscribe, () => current);
